import React from "react"
import * as Yup from "yup"
import {
  Box, Button, Text,
} from "@chakra-ui/react"
import { Controller } from "react-hook-form"
import { useQueryClient } from "react-query"
import { useShallow } from "zustand/shallow"
import {
  EventGroup, Filter, ModalName, Segment, SegmentRuleField, SegmentRuleOperator,
} from "sharedTypes"
import { deleteSegment, postSegment, putSegment } from "api/guests"
import {
  addSegment, refetchGuests, removeSegment, updateSegment,
} from "context/actions"
import useModal from "services/useModal"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import UpgradeAlert from "components/elements/UpgradeAlert"
import Icon from "components/elements/Icon"
import SegmentColorPicker from "pages/EventShow/Guests/TableView/Filters/SegmentColorPicker"
import ChipRow from "pages/EventShow/Guests/TableView/Filters/ChipRow"
import { chipToRule, ruleValueToChipValues } from "utilities/chipRules"
import useGuestsStore from "../services/useGuestsStore"

type Props = {
  eventId: string;
  segmentsEnabled: boolean;
  segmentId: string;
  segments: Segment[]
  eventGroups: EventGroup[]
  onHide: () => void;
}

type FormValues = {
  name: string
  color: string
  chips: Filter[]
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  color: Yup.string().required(),
  // At least one chip required — empty conditions would match everything,
  // which isn't a segment.
  chips: Yup.array().min(1),
})

// Lifts a persisted segment's rules into the chip shape the new chip system
// consumes. Stable, index-derived ids let React reconcile across edits in the
// modal — once the user mutates them, ChipRow's generateChipId takes over.
const segmentRulesToChips = (segment: Segment): Filter[] => (
  segment.rules.map((rule, i) => ({
    id: `segment-${segment.id}-${i}`,
    field: rule.field as SegmentRuleField,
    operator: rule.operator as SegmentRuleOperator,
    values: ruleValueToChipValues(rule.value),
  }))
)

const SegmentForm = ({
  eventId, segmentsEnabled, segmentId, segments, eventGroups, onHide,
}: Props) => {
  const queryClient = useQueryClient()
  const {
    filter, searchString, groupId, order,
  } = useGuestsStore(
    useShallow((state) => ({
      filter: state.filter,
      searchString: state.searchString,
      groupId: state.groupId,
      order: state.order,
    })),
  )

  const showModal = useModal()

  const existingSegment = segmentId
    ? segments.find(({ id }) => segmentId === id)
    : undefined

  const initialValues: FormValues = existingSegment ? {
    name: existingSegment.name,
    color: existingSegment.color,
    chips: segmentRulesToChips(existingSegment),
  } : {
    name: "",
    color: "gray",
    chips: [],
  }

  const activate = (segment: Segment) => {
    filter({ segmentId: segment.id })
    onHide()
  }

  const persist = ({ name, color, chips }: FormValues) => {
    const rules = chips.map(chipToRule)
    if (existingSegment) {
      return putSegment(eventId, {
        ...existingSegment, name, color, rules,
      }).then(({ data }) => {
        updateSegment(queryClient, eventId, data)
        refetchGuests(queryClient, eventId, searchString, groupId, order, segmentId)
        activate(data)
      })
    }

    return postSegment(eventId, { name, color, rules }).then(({ data }) => {
      addSegment(queryClient, eventId, data)
      activate(data)
    })
  }

  const onSubmit = segmentsEnabled
    ? persist
    : async () => { showModal(ModalName.Upgrade) }

  const onDelete = existingSegment
    ? () => {
      deleteSegment(eventId, segmentId).then(() => {
        removeSegment(queryClient, eventId, segmentId)
        onHide()
      })
    }
    : undefined

  const title = existingSegment ? "Edit Segment" : "Add Segment"

  return (
    <ModalForm
      title={title}
      onClose={onHide}
      onSubmit={onSubmit}
      onDanger={onDelete}
      dangerProps={{ type: "Segment", name: initialValues.name }}
      initialValues={initialValues}
      validationSchema={segmentsEnabled ? validationSchema : undefined}
      size="xl"
      disableSubmitWhenNotDirty
    >
      {({ control, watch }) => {
        const color = (watch("color") as string) || "gray"

        return (
          <>
            <Text mb={6}>
              Use Segments to automatically group guests based on
              custom conditions and filters throughout your event data.
            </Text>

            <Box mb={8}>
              <TextField
                name="name"
                control={control}
                variant="flushed"
                size="xl"
                placeholder="Segment Name"
                ariaLabel="Segment Name"
                h={12}
                leftElement={<Icon icon="dot" size={5} color={`${color}.500`} />}
                mb={0}
              />
            </Box>

            <Box mb={8}>
              <Text fontSize="md" fontWeight="semibold" mb={3}>Color</Text>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <SegmentColorPicker value={field.value} onChange={field.onChange} />
                )}
              />
            </Box>

            <Box mb={8}>
              <Text fontSize="md" mb={5}>
                Guests matching{" "}
                <Box as="span" fontWeight="semibold">all</Box>
                {" "}of the following rules
              </Text>
              <Controller
                name="chips"
                control={control}
                render={({ field }) => (
                  <ChipRow
                    eventId={eventId}
                    chips={field.value}
                    onChange={field.onChange}
                    eventGroups={eventGroups}
                    direction="vertical"
                    size="lg"
                    addTrigger={(
                      <Button
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Icon icon="plus" size={3} color="blue.500" />}
                      >
                        Add Condition
                      </Button>
                    )}
                  />
                )}
              />
            </Box>

            {!segmentsEnabled && (
              <Box mt={4}>
                <UpgradeAlert text="This feature requires a Corporate Subscription." />
              </Box>
            )}
          </>
        )
      }}
    </ModalForm>
  )
}

export default SegmentForm
